// PRD 11 §2.2 #15 — Grade radio. Single-select G1/G2/G3, each option showing the stage
// badge plus its description (예방적 관리 / 자가처치 / 의료진 상담).
//
// SHCT-841 — the option label is the SAME `.grade grade-gN` badge the list row uses
// (prototype consultation-manual.html #manModal), not bare colored text. Colors come from
// the shared --grade-gN tokens (PRD 07 §4 "공유 .grade 토큰 … PRO항목설계와 동일",
// v1.7 2026.06.11 색상 정정), which PRD 11 §1 adopts by reference.

import { useTranslation } from 'react-i18next';

import { MANUAL_GRADES, type ManualGrade } from '../types/counseling-manual.types';

// Radio accent = the badge's own token, so the control and its badge read as one colour.
const ACCENTS: Record<ManualGrade, string> = {
  G1: 'var(--grade-g1)',
  G2: 'var(--grade-g2)',
  G3: 'var(--grade-g3)',
};

interface GradeRadioGroupProps {
  value: ManualGrade | null;
  onChange: (next: ManualGrade) => void;
  invalid?: boolean;
  disabled?: boolean;
}

export function GradeRadioGroup({ value, onChange, invalid, disabled }: GradeRadioGroupProps) {
  const { t } = useTranslation('counseling-manual');

  return (
    // Prototype parity: the three options share one row (each `flex:1`, no wrap)
    // with the stage label stacked above its description and both `nowrap`, so a
    // long description can never push an option onto a second line.
    <div
      style={{ display: 'flex', gap: 10, marginTop: 4 }}
      role="radiogroup"
      aria-invalid={invalid || undefined}
    >
      {MANUAL_GRADES.map((grade) => {
        const selected = value === grade;
        return (
          <label
            key={grade}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              cursor: disabled ? 'not-allowed' : 'pointer',
              padding: '10px 12px',
              border: `1.5px solid ${selected ? ACCENTS[grade] : 'var(--border)'}`,
              borderRadius: 'var(--radius-sm)',
              background: selected ? '#fff' : 'transparent',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <input
              type="radio"
              name="manual-grade"
              value={grade}
              checked={selected}
              onChange={() => onChange(grade)}
              disabled={disabled}
              style={{ accentColor: ACCENTS[grade], flex: '0 0 auto' }}
            />
            <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <span className={`grade grade-${grade.toLowerCase()}`} style={{ whiteSpace: 'nowrap' }}>
                {t(`gradeLabels.${grade}`)}
              </span>
              <span style={{ fontSize: 12, color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>
                {t(`gradeDescriptions.${grade}`)}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}
